/**
 * useReactionMute — per-(game, player_idx) mute set, persisted in
 * localStorage so a refresh doesn't undo the user's preference.
 *
 * Scope: per game id, by slot index. Resets when the user opens a new
 * game. We don't try to persist by user_id across games — that needs
 * a backend round-trip and the per-game scope is what users actually
 * want ("I don't want to see THIS guy's emoji spam during this match").
 *
 * Storage key: `mm.muted.<gameId>` → JSON array of int player indices.
 *
 * Usage:
 *   const { isMuted, toggleMute, mutedSet } = useReactionMute(gameId);
 *   <ReactionBar isMuted={isMuted} ... />
 *   <LiveScoreboard mute={{ isMuted, toggleMute, currentPlayerIdx }} ... />
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const KEY_PREFIX = "mm.muted.";


function readPersisted(gameId) {
  if (!gameId) return new Set();
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + gameId);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr.map(Number).filter(Number.isFinite) : []);
  } catch {
    return new Set();
  }
}

function writePersisted(gameId, set) {
  if (!gameId) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + gameId, JSON.stringify([...set]));
  } catch {
    // localStorage full / disabled — silently drop. Mute is a UX nicety,
    // not a security feature.
  }
}


export function useReactionMute(gameId) {
  const [mutedSet, setMutedSet] = useState(() => readPersisted(gameId));

  // Re-read when the game id changes (route change, new game).
  useEffect(() => {
    setMutedSet(readPersisted(gameId));
  }, [gameId]);

  const toggleMute = useCallback((playerIdx) => {
    setMutedSet((prev) => {
      const next = new Set(prev);
      if (next.has(playerIdx)) next.delete(playerIdx);
      else next.add(playerIdx);
      writePersisted(gameId, next);
      return next;
    });
  }, [gameId]);

  const isMuted = useCallback((playerIdx) => mutedSet.has(playerIdx), [mutedSet]);

  return useMemo(
    () => ({ isMuted, toggleMute, mutedSet }),
    [isMuted, toggleMute, mutedSet],
  );
}
